import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { User, PencilSimple, Crown, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/admin/users?page=${page}&limit=20`);
        setUsers(res.data.users || []);
        setTotalPages(res.data.pages || 1);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [page]);

  const updateUser = async (field, value) => {
    if (!selectedUser) return;

    try {
      const res = await axios.put(`${API}/admin/users/${selectedUser.user_id}`, {
        [field]: value
      });

      setUsers(prev => prev.map(u => u.user_id === selectedUser.user_id ? res.data : u));
      setSelectedUser(res.data);
      toast.success("User updated");
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const makeAdmin = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, {
        role: "admin"
      });

      setUsers(prev => prev.map(u => u.user_id === userId ? res.data : u));
      toast.success("User promoted to Admin with Premium access");
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const removeAdmin = async (userId) => {
    try {
      const res = await axios.put(`${API}/admin/users/${userId}`, {
        role: "user",
        subscription_tier: "free"
      });

      setUsers(prev => prev.map(u => u.user_id === userId ? res.data : u));
      toast.success("Admin role removed");
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const grantTrial = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Grant 14-day Premium trial to ${selectedUser.name || selectedUser.email}? They will get full Premium access for 14 days, then auto-downgrade to Free. A welcome email will be sent.`)) {
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUser.user_id}/grant-trial`);
      setUsers(prev => prev.map(u => u.user_id === selectedUser.user_id ? res.data : u));
      setSelectedUser(res.data);
      toast.success(`14-day trial granted. Ends ${new Date(res.data.trial_ends_at).toLocaleDateString()}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant trial");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-users-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-zinc-400 mt-1">Manage user accounts and subscriptions</p>
        </div>

        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="loading-spinner" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id} className="border-zinc-800">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                              <User className="w-4 h-4 text-zinc-400" />
                            </div>
                          )}
                          <span className="font-medium">{user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-400">{user.email}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-mono uppercase ${
                          user.role === 'admin' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-zinc-800 text-zinc-400'
                        }`}>
                          {user.role}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-sm text-xs font-mono uppercase ${
                          user.subscription_tier === 'pro' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-zinc-800 text-zinc-400'
                        }`}>
                          {user.subscription_tier}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-400">
                        {user.nonprofit_profile?.org_name || "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {user.role !== 'admin' ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => makeAdmin(user.user_id)}
                              className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10"
                              data-testid={`make-admin-${user.user_id}`}
                            >
                              <Crown className="w-4 h-4 mr-1" />
                              Make Admin
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => removeAdmin(user.user_id)}
                              className="text-zinc-400 hover:text-zinc-300"
                              data-testid={`remove-admin-${user.user_id}`}
                            >
                              Remove Admin
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => { setSelectedUser(user); setEditDialogOpen(true); }}
                            data-testid={`edit-user-${user.user_id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button 
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="border-zinc-800"
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-400 px-4">
              Page {page} of {totalPages}
            </span>
            <Button 
              variant="outline"
              size="sm"
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="border-zinc-800"
            >
              Next
            </Button>
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {selectedUser && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-4 mb-4">
                  {selectedUser.picture ? (
                    <img src={selectedUser.picture} alt="" className="w-16 h-16 rounded-full" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-zinc-800 flex items-center justify-center">
                      <User className="w-8 h-8 text-zinc-400" />
                    </div>
                  )}
                  <div>
                    <p className="font-bold text-lg">{selectedUser.name}</p>
                    <p className="text-zinc-400 text-sm">{selectedUser.email}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Role</label>
                  <Select 
                    value={selectedUser.role} 
                    onValueChange={(v) => updateUser("role", v)}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin (includes Premium)</SelectItem>
                    </SelectContent>
                  </Select>
                  {selectedUser.role !== "admin" && (
                    <p className="text-xs text-zinc-500 mt-1">
                      Setting role to Admin automatically grants Premium tier access
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Subscription Tier</label>
                  <Select 
                    value={selectedUser.subscription_tier} 
                    onValueChange={(v) => updateUser("subscription_tier", v)}
                    disabled={selectedUser.role === "admin"}
                  >
                    <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-tier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                  {selectedUser.role === "admin" && (
                    <p className="text-xs text-emerald-400 mt-1">
                      Admins always have Premium tier access
                    </p>
                  )}
                </div>

                {/* Trial section */}
                <div className="space-y-2 border-t border-zinc-800 pt-4">
                  <label className="text-sm text-zinc-400">14-Day Premium Trial</label>
                  {selectedUser.trial_status === "active" ? (
                    <div className="bg-emerald-950/40 border border-emerald-800 rounded-md p-3" data-testid="trial-active-info">
                      <p className="text-emerald-400 text-sm font-semibold">Trial active</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        Ends {selectedUser.trial_ends_at ? new Date(selectedUser.trial_ends_at).toLocaleString() : "—"}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        Flash Drafts used: {selectedUser.trial_flash_drafts_used || 0}/3
                      </p>
                    </div>
                  ) : selectedUser.trial_status === "ended" ? (
                    <p className="text-xs text-zinc-500">Trial already used (ended {selectedUser.trial_ends_at ? new Date(selectedUser.trial_ends_at).toLocaleDateString() : ""}).</p>
                  ) : selectedUser.role === "admin" ? (
                    <p className="text-xs text-zinc-500">Admins already have permanent Premium — trial not needed.</p>
                  ) : (
                    <>
                      <Button
                        onClick={grantTrial}
                        className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold w-full"
                        data-testid="grant-trial-btn"
                      >
                        <Sparkle weight="fill" className="w-4 h-4 mr-2" />
                        Grant 14-Day Premium Trial
                      </Button>
                      <p className="text-xs text-zinc-500">
                        No card required. Auto-downgrades to Free on day 15. Sends welcome email.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
